import {
  removeQueuedSubmission,
  listQueuedSubmissions,
  updateQueuedSubmission,
  readAttachment,
  deleteAttachment,
  recordSyncIssue,
} from "./db";
import type { SubmissionQueueEntry } from "./db";
import { API_BASE_URL } from "../api/http";
import { isRuntimeOfflineEnabled } from "./runtime";
import { subscribeOfflineRuntime } from "./runtime";
import { documentUploadApi } from "../api/handlers/documentUpload";
import type { PendingAttachmentMetadata } from "./formMetadata";

type SyncResult = {
  processed: number;
  failed: number;
  issues: number;
  errors: string[];
};

export const CONNECTIVITY_RESTORED_EVENT = "offline-connectivity-restored";
export const OFFLINE_SYNC_COMPLETED_EVENT = "offline-sync-completed";

let syncing = false;
const OFFLINE_ATTACHMENT_PREFIX = "offline://attachment/";

const replaceAll = (value: string, search: string, replacement: string) =>
  value.split(search).join(replacement);

const replaceOfflineAttachmentUrls = async (entry: SubmissionQueueEntry & { id: number }) => {
  if (!entry.body) return entry;
  let bodyString: string | null = null;
  let originalWasString = false;
  if (typeof entry.body === "string") {
    bodyString = entry.body;
    originalWasString = true;
  } else if (entry.body && typeof entry.body === "object" && !(entry.body instanceof Blob)) {
    try {
      bodyString = JSON.stringify(entry.body);
    } catch (error) {
      bodyString = null;
    }
  }

  if (!bodyString) {
    return entry;
  }

  const placeholderRegex = /offline:\/\/attachment\/([A-Za-z0-9_-]+)/g;
  const matches = [...bodyString.matchAll(placeholderRegex)];
  if (!matches.length) {
    return entry;
  }

  const replacements = new Map<string, string>();
  for (const match of matches) {
    const placeholder = match[0];
    if (replacements.has(placeholder)) continue;
    const attachmentId = match[1];
    const stored = await readAttachment(attachmentId);
    if (!stored) continue;
    const fileName = (stored.metadata as { fileName?: string } | undefined)?.fileName ?? attachmentId;
    const contentType = stored.mimeType || "application/octet-stream";
    const blob = new Blob([stored.data], { type: contentType });
    const file = new File([blob], fileName, { type: contentType });
    const upload = await documentUploadApi.upload(file);
    await deleteAttachment(attachmentId);
    replacements.set(placeholder, upload.url);
  }

  if (!replacements.size) {
    return entry;
  }

  let updatedString = bodyString;
  replacements.forEach((url, placeholder) => {
    updatedString = replaceAll(updatedString, placeholder, url);
  });

  entry.body = originalWasString ? updatedString : JSON.parse(updatedString);
  return entry;
};

const processPendingAttachments = async (entry: SubmissionQueueEntry & { id: number }) => {
  await replaceOfflineAttachmentUrls(entry);
  const metadata = entry.metadata;
  const pending = metadata && typeof metadata === "object" && Array.isArray((metadata as Record<string, unknown>).pendingAttachments)
    ? ((metadata as { pendingAttachments: PendingAttachmentMetadata[] }).pendingAttachments)
    : [];
  if (!pending || pending.length === 0) {
    return entry;
  }

  let bodyObject: any = null;
  let bodyIsString = false;
  if (entry.body && typeof entry.body === "string") {
    try {
      bodyObject = JSON.parse(entry.body);
      bodyIsString = true;
    } catch {
      bodyObject = null;
    }
  } else if (entry.body && typeof entry.body === "object" && !(entry.body instanceof Blob)) {
    bodyObject = { ...(entry.body as Record<string, unknown>) };
  }

  let bodyDirty = false;

  for (const attachmentMeta of pending) {
    const stored = await readAttachment(attachmentMeta.attachmentId);
    if (!stored) {
      continue;
    }
    const fileName =
      attachmentMeta.fileName ||
      (stored.metadata && typeof stored.metadata === "object" ? (stored.metadata as { fileName?: string })?.fileName : null) ||
      attachmentMeta.attachmentId;
    const contentType = attachmentMeta.contentType || stored.mimeType || "application/octet-stream";
    const blob = new Blob([stored.data], { type: contentType });
    const file = new File([blob], fileName ?? "Document", { type: contentType });
    const upload = await documentUploadApi.upload(file);
    if (bodyObject && typeof bodyObject.notes === "string" && attachmentMeta.placeholderUrl) {
      bodyObject.notes = replaceAll(bodyObject.notes, attachmentMeta.placeholderUrl, upload.url);
      bodyDirty = true;
    }
    await deleteAttachment(attachmentMeta.attachmentId);
  }

  if (bodyDirty && bodyObject) {
    entry.body = bodyIsString ? JSON.stringify(bodyObject) : bodyObject;
  }
  if (metadata && typeof metadata === "object") {
    delete (metadata as Record<string, unknown>).pendingAttachments;
  }
  await updateQueuedSubmission(entry.id, { body: entry.body, metadata: entry.metadata });
  return entry;
};

const buildHeaders = (headers?: HeadersInit) => {
  const next = new Headers(headers);
  if (!next.has("Accept")) {
    next.set("Accept", "application/json");
  }
  const token = typeof window !== "undefined" ? window.localStorage.getItem("auth_token") : null;
  if (token && token.trim().length) {
    const normalized = token.trim();
    next.set("Authorization", /^bearer\s/i.test(normalized) ? normalized : `Bearer ${normalized}`);
  }
  return next;
};

const handleSyncConflict = async (entry: SubmissionQueueEntry & { id: number }, error: Error & { status?: number }) => {
  if (error?.status !== 409 && error?.status !== 412) {
    return false;
  }
  await recordSyncIssue({
    endpoint: entry.endpoint,
    method: entry.method,
    resource: entry.resource,
    body: entry.body,
    metadata: entry.metadata ?? null,
    error: error?.message ?? "Duplicate detected during sync.",
    createdAt: Date.now(),
  });
  await removeQueuedSubmission(entry.id);
  return true;
};

const executeQueueEntry = async (entry: SubmissionQueueEntry & { id: number }) => {
  const url = `${API_BASE_URL}${entry.endpoint}`;
  const headers = buildHeaders(entry.headers);
  const bodyIsObject = entry.body && typeof entry.body === "object" && !(entry.body instanceof Blob);
  if (bodyIsObject && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: entry.method,
    headers,
    body: bodyIsObject ? JSON.stringify(entry.body) : (entry.body as BodyInit | undefined),
  };
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    const message = "Unable to reach the server. Check your internet connection and try again.";
    const networkError = new Error(message);
    (networkError as Error & { status?: number }).status = undefined;
    throw networkError;
  }
  if (!response.ok) {
    const text = await response.text();
    const message = text || response.statusText || "Request failed";
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return response;
};

export const syncPendingQueue = async (): Promise<SyncResult> => {
  if (syncing) {
    return { processed: 0, failed: 0, issues: 0, errors: [] };
  }
  syncing = true;
  try {
    const entries = await listQueuedSubmissions("pending");
    let processed = 0;
    let failed = 0;
    let issues = 0;
    const errors: string[] = [];
    for (const entry of entries) {
      try {
        await updateQueuedSubmission(entry.id!, { status: "processing", lastAttemptAt: Date.now() });
        const preparedEntry = await processPendingAttachments(entry as SubmissionQueueEntry & { id: number });
        await executeQueueEntry(preparedEntry);
        await removeQueuedSubmission(entry.id!);
        processed += 1;
      } catch (error: any) {
        const status = error?.status as number | undefined;
        const message = error?.message ?? "Request failed";
        const handledConflict = await handleSyncConflict(entry as SubmissionQueueEntry & { id: number }, error as Error & {
          status?: number;
        });
        if (handledConflict) {
          processed += 1;
          issues += 1;
          if (!errors.includes(message)) {
            errors.push(message);
          }
          continue;
        }
        failed += 1;
        await updateQueuedSubmission(entry.id!, {
          status: status === 409 || status === 412 ? "failed" : "pending",
          retryCount: (entry.retryCount ?? 0) + 1,
          lastAttemptAt: Date.now(),
          error: message,
        });
        if (!errors.includes(message)) {
          errors.push(message);
        }
      }
    }
    if (typeof window !== "undefined" && (processed > 0 || failed > 0)) {
      window.dispatchEvent(
        new CustomEvent(OFFLINE_SYNC_COMPLETED_EVENT, {
          detail: { processed, failed, errors, issues },
        }),
      );
    }
    return { processed, failed, errors, issues };
  } finally {
    syncing = false;
  }
};

const notifyConnectivityRestored = () => {
  if (typeof window === "undefined") return;
  if (!navigator.onLine) return;
  if (!isRuntimeOfflineEnabled()) return;
  window.dispatchEvent(new CustomEvent(CONNECTIVITY_RESTORED_EVENT));
  void syncPendingQueue().catch((error) => {
    console.warn("Background sync failed", error);
  });
};

if (typeof window !== "undefined") {
  window.addEventListener("online", notifyConnectivityRestored);
  subscribeOfflineRuntime(() => notifyConnectivityRestored());
}
